import { useTheme } from '@mui/material';
import { type FC, useMemo } from 'react';

import { hasValue } from '@core/utils/equality';

import { createPinInfoContent, createPinMarkerContent } from '../SlabMap/mapMarkerContent';
import type { HasMap } from '../SlabMap/SlabMap';
import { SlabMarker } from '../SlabMap/SlabMarker';
import type { LocationPin } from './mapPins';

export type PinMarkersProps = Partial<HasMap> & {
  onClickPin?: (pin: LocationPin) => void;
};

type Props = PinMarkersProps & {
  pins: LocationPin[];
};

export const PinMarkers: FC<Props> = ({ pins, map, onClickPin }) => {
  const theme = useTheme();

  const markers = useMemo(
    () =>
      pins.map((pin) => ({
        pin,
        location: { lat: pin.lat, lng: pin.lng },
        content: createPinMarkerContent(pin.color, theme),
        infoContent: createPinInfoContent(pin.name, pin.address),
      })),
    [pins, theme],
  );

  // `SlabMap` only injects `map` into its direct children, so each marker has to
  // be handed the map this component received.
  return (
    <>
      {markers.map(({ pin, location, content, infoContent }) => (
        <SlabMarker
          key={pin.id}
          map={map}
          location={location}
          title={pin.name}
          clusterMeta={{ locationType: pin.type, color: pin.color }}
          content={content}
          infoContent={infoContent}
          onClick={hasValue(onClickPin) ? (): void => onClickPin(pin) : undefined}
        />
      ))}
    </>
  );
};
